import React, { useState, useRef, useEffect } from 'react';
import { ClassValue } from 'classnames/types';
import classNames from 'classnames';

import { SupportedIconName } from '../../shared/icons';
import BaseInput, { BaseInputPropsWithoutChildren } from '../Input/BaseInput';
import OptionList, { OptionListOption } from '../OptionList/OptionList';
import styles from './Autocomplete.module.scss';
import isIE11 from '../../shared/utils/isIE11/isIE11';
import { ArrayElementType } from '../../types/utils/ArrayElementType';
import { CreateProps } from '../../types/utils/CreateProps';

type AutocompleteProps = CreateProps<
  {
    /** Label shown on the autocomplete */
    label?: string;
    /** List of options */
    options: OptionListOption[];
    /** Selected option value to be shown in the autocomplete */
    value?: string;
    /** OnChange handler when user types in the autocomplete input or makes a selection, will return the value and native event (value, event) */
    onChange: (
      event: React.SyntheticEvent<HTMLDivElement>,
      option: string
    ) => void;
    /** onOptionSelect is called when user makes selection from the list. It will return whole option object and native event (option, event)*/
    onOptionSelect?: (
      event: React.SyntheticEvent<HTMLDivElement>,
      option: OptionListOption
    ) => void;
    /** Type of the autocomplete value */
    type?: SupportedInputType;
    /** Placeholder is visible when the autocomplete is empty */
    placeholder?: string;
    /** Sets the autocomplete in readonly state. Autocomplete is clickable and focusable but user can't type in it */
    readonly?: boolean;
    /** Icon to be displayed. Is not visible if input is in the errornous or confirmed state  */
    iconName?: SupportedIconName;
    /** OnClick handler for the icon */
    onIconClick?: () => void;
    /** Custom id */
    id?: string;
    /** Custom className */
    className?: ClassValue;
    children?: undefined;
  },
  BaseInputPropsWithoutChildren
>;

const Autocomplete: React.FunctionComponent<AutocompleteProps> = ({
  options,
  onChange,
  value,
  errorText,
  disabled,
  onOptionSelect,
  ...props
}) => {
  const [open, setOpen] = useState(false);
  const [optionIsSelected, setOptionIsSelected] = useState(false);
  const [focusedOptionId, setFocusedOptionId] = useState<string>();

  const inputElement = useRef<HTMLInputElement>(null);
  const autocompleteElement = useRef<HTMLDivElement>(null);

  const focusOnInput = () => {
    inputElement.current?.focus();
  };

  useEffect(() => {
    if (options[0]?.label === value) {
      setOpen(false);
    } else if (options.length > 0 && value && !optionIsSelected) {
      setOpen(true);
    } else {
      setOpen(false);
      setFocusedOptionId(undefined);
      setOptionIsSelected(false);
    }
  }, [value, options]);

  const handleOptionListBlur = (
    event:
      | React.FocusEvent<HTMLDivElement>
      | React.KeyboardEvent<HTMLDivElement>
  ) => {
    const isFocusEvent = (
      e: typeof event
    ): e is React.FocusEvent<HTMLDivElement> => 'relatedTarget' in e;

    /* istanbul ignore else  */
    if (
      !isFocusEvent(event) ||
      !autocompleteElement.current?.contains(event.relatedTarget as HTMLElement)
    ) {
      setOpen(false);
      setFocusedOptionId(undefined);
      focusOnInput();
    }
  };

  const handleOnBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    /* istanbul ignore else */
    if (!isIE11) {
      /* istanbul ignore else  */
      if (
        !autocompleteElement.current?.contains(
          e.relatedTarget as HTMLElement
        ) &&
        open
      ) {
        setOpen(false);
        setFocusedOptionId(undefined);
      }
    }

    /* istanbul ignore next */
    if (isIE11 && typeof document !== 'undefined') {
      /* Prevent the onBlur in IE if user has clicked an option from the list or the scrollbar inside the optionlist */
      if (
        document.activeElement &&
        document.activeElement.className.indexOf('telia-react-optionlist') < 0
      ) {
        setOpen(false);
        setFocusedOptionId(undefined);
      }
    }
  };

  const handleOptionSelect = (
    event: React.SyntheticEvent<HTMLDivElement>,
    option: OptionListOption
  ) => {
    focusOnInput();
    setOpen(false);
    setOptionIsSelected(true);
    setFocusedOptionId(undefined);
    onOptionSelect && onOptionSelect(event, option);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event, event.target.value);
  };

  const keyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (options.length === 0) {
      return;
    }

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      /* istanbul ignore else  */
      if (!open) {
        setOpen(true);
      }

      if (
        event.target === inputElement.current ||
        (event.target as any) === 'autocompleteTestEntryPoint'
      ) {
        if (event.key === 'ArrowDown') {
          const firstNotDisabledOption = options.find(
            (option) => !!option.disabled === false
          );
          setFocusedOptionId(firstNotDisabledOption?.value);
        }

        if (event.key === 'ArrowUp') {
          const lastNotDisabledOption = options
            .slice()
            .reverse()
            .find((option) => !!option.disabled === false);
          setFocusedOptionId(lastNotDisabledOption?.value);
        }
      }
    }
  };

  return (
    <div
      className={classNames([
        styles.autocomplete,
        {
          [styles.autocomplete_open]: open,
        },
      ])}
      ref={autocompleteElement}
      onKeyDown={keyDown}
      onBlur={handleOnBlur}
    >
      <BaseInput
        {...props}
        disabled={disabled}
        value={value || undefined}
        onChange={disabled ? undefined : handleInputChange}
        onClick={disabled ? undefined : focusOnInput}
        ref={inputElement}
        errorText={errorText && errorText === ' ' ? ' ' : errorText}
      />
      {!!open && (
        <OptionList
          options={options}
          onBlur={handleOptionListBlur}
          optionOnClick={handleOptionSelect}
          focusedOptionId={focusedOptionId}
        />
      )}
    </div>
  );
};

export const supportedInputTypes = [
  'text',
  'password',
  'email',
  'tel',
  'search',
] as const;

export type SupportedInputType = ArrayElementType<typeof supportedInputTypes>;

export default Autocomplete;
